// @ts-check
import './tooltip.css';
import { Fragment, h } from 'preact';
import { Children, cloneElement } from 'preact/compat';
import { useRef, useState, useEffect } from 'preact/hooks';
import { createPortal } from 'react-dom';

import useResizeObserver from '@react-hook/resize-observer';

/**
 * @param {DOMRect} childRect
 * @param {DOMRect} toolTipRect
 * @param {{ width: number, height: number }} elementSize
 * @param {{ width: number, height: number }} svgSize
 * @returns {{ x: number, y: number, offset: number, isAboveChild: boolean }}
*/
const calcPosition = (childRect, toolTipRect, elementSize, svgSize) => {
	const offset = 15;
	const arrowOffset = 20;
	const svgScale = elementSize.width / svgSize.width;
	const initialX = ((childRect.x + childRect.width / 2) * svgScale) - toolTipRect.width / 2;
	let x = Math.min(initialX, elementSize.width - toolTipRect.width - offset);
	x = Math.max(x, 0 + offset);

	const initialY = (childRect.y * svgScale) - (toolTipRect.height + arrowOffset);
	const y = initialY - offset < 0 ? ((childRect.y + childRect.height) * svgScale) + arrowOffset : initialY;

	return {
		x,
		y,
		offset: initialX - x,
		isAboveChild: initialY - offset >= 0
	};
};

/**
 * @param {object} props
 * @param {import('preact').ComponentChildren} props.children
 * @param {import('preact').ComponentChildren} props.content
 * @param {HTMLElement=} props.rootNode
 * @param {{ width: number, height: number }} props.svgSize
 * @returns
 */
export default function Tooltip({ children, content, rootNode, svgSize }) {
	const [elementSize, setElementSize] = useState({ width: rootNode?.offsetWidth, height: rootNode?.offsetHeight });
	useResizeObserver(rootNode || null, entry => {
		const { inlineSize: width, blockSize: height } = entry.contentBoxSize[0];
		setElementSize({ width, height });
	});

	const toolTipRef = useRef(null);
	const childrenRef = useRef([]);
	const [transform, setTransform] = useState(null);
	const [over, onOver] = useState(false);
	const [arrowOffset, setArrowOffset] = useState(0);
	const [isAboveChild, setAboveChild] = useState(true);

	useEffect(() => {
		const [firstChild] = childrenRef?.current;

		if (!firstChild || !toolTipRef?.current) {
			return;
		}
		const childRect = firstChild.base.getBBox();
		const toolTipRect = toolTipRef.current.getBoundingClientRect();

		const { x, y, offset, isAboveChild } = calcPosition(childRect, toolTipRect, elementSize, svgSize);

		setArrowOffset(offset);
		setTransform(`translate(${x}px, ${y}px)`);
		setAboveChild(isAboveChild);
	}, [childrenRef, toolTipRef, elementSize.width, elementSize.height, svgSize.width, svgSize.height]);

	const onMouseEnter = () => onOver(true);
	const onMouseLeave = () => onOver(false);

	return (
		<Fragment>
			{Children.map(children, (child, index) =>
				cloneElement(child, {
					onMouseEnter,
					onMouseLeave,
					ref: (ref) => (childrenRef.current[index] = ref)
				})
			)}
			{createPortal(
				<span
					ref={toolTipRef}
					role="tooltip"
					style={transform ? { transform }: undefined}
					className={`tooltip-container${over ? ' active' : ''}${!isAboveChild ?  ' is-below-child' : ''}`}
				>
					<span className="tooltip-arrow" style={{ left: `calc(50% + ${arrowOffset}px)` }}></span>
					{content}
				</span>,
				rootNode || document.body
			)}
		</Fragment>
	);
}
